import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

import { apiRequest, ApiError } from "../api/client";
import type { CurrentUser } from "../types";

interface AuthContextValue {
  user: CurrentUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  hasRole: (...roles: string[]) => boolean;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const currentUser = await apiRequest<CurrentUser>("/auth/me");
      setUser(currentUser);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setUser(null);
      } else {
        throw error;
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const logout = async () => {
    await apiRequest("/auth/logout", { method: "POST" });
    setUser(null);
    window.location.href = "/login";
  };

  const hasRole = (...roles: string[]) => user !== null && roles.some((r) => user.roles.includes(r));

  return (
    <AuthContext.Provider
      value={{ user, loading, isAuthenticated: user !== null, hasRole, logout, refresh }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider.");
  }
  return context;
}
